/**
 * DE-355 — flyout display cache (ATC seed + slim catalog fill).
 * Basket line JSON is enough to paint most rows; this cache stores catalog
 * stubs so open / remove do not refetch products we already showed.
 *
 * Line reseeds must not wipe thumb / inventory / GA4 fields from a prior
 * catalog hydrate — otherwise remove triggers another getProducts.
 */

import {GET_PRODUCTS_STALE_TIME_MS} from '@amblique/king-living/app/utils/api-utils';
import {
  parseCylindoUrlFromSelectedOptionsUI,
  parseSelectedOptionsUIPayload,
} from '@amblique/king-living/app/utils/cart-utils';

export const CART_FLYOUT_DISPLAY_QUERY_KEY = ['cartFlyoutDisplay'];

export const CART_FLYOUT_DISPLAY_STALE_TIME_MS = GET_PRODUCTS_STALE_TIME_MS;

/**
 * @param {Object|null|undefined} line
 * @returns {boolean}
 */
export function basketLineHasFlyoutThumb(line) {
  if (!line) return false;

  if (parseCylindoUrlFromSelectedOptionsUI(line.c_selectedOptionsUI)) {
    return true;
  }

  const groups = line.imageGroups;

  return Boolean(Array.isArray(groups) && groups.some((group) => group?.images?.[0]?.link));
}

/**
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function catalogProductHasFlyoutThumb(product) {
  const groups = product?.imageGroups;

  return Boolean(Array.isArray(groups) && groups.some((group) => group?.images?.[0]?.link));
}

/**
 * Thin basket/ATC seed — id/name/image/inventory only. Catalog `getProducts`
 * payloads carry `type` / GA4 custom attrs; stubs must not suppress GA4 fetch.
 *
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function isFlyoutDisplayStub(product) {
  if (!product) return true;

  if (product.type != null) return false;

  if (product.c_ga4CategoryPath != null) return false;

  if (product.brand != null || product.slugUrl != null) return false;

  return true;
}

/**
 * @param {Object|null|undefined} line
 * @returns {Object|null}
 */
export function buildFlyoutDisplayProductFromLine(line) {
  const productId = line?.productId;

  if (!productId) return null;

  const cylindoUrl = parseCylindoUrlFromSelectedOptionsUI(line.c_selectedOptionsUI);
  const imageGroups = cylindoUrl ?
    [{viewType: 'large', images: [{link: cylindoUrl}]}] :
    (line.imageGroups ?? undefined);

  return {
    id: productId,
    name: line.productName || line.itemText || '',
    shortDescription: line.shortDescription || null,
    imageGroups,
    inventory: line.inventory,
  };
}

/**
 * @param {Object[]|null|undefined} lines
 * @returns {Object<string, Object>}
 */
export function buildFlyoutDisplayMapFromLines(lines = []) {
  return (lines ?? []).reduce((acc, line) => {
    const product = buildFlyoutDisplayProductFromLine(line);

    if (product) {
      acc[product.id] = product;
    }

    const masterProductId = parseSelectedOptionsUIPayload(line?.c_selectedOptionsUI)?.masterProductId;

    if (masterProductId && !acc[masterProductId]) {
      acc[masterProductId] = {
        id: masterProductId,
        name: line?.productName || product?.name || '',
        shortDescription: null,
        imageGroups: product?.imageGroups,
      };
    }

    return acc;
  }, {});
}

/**
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function catalogProductHasInventory(product) {
  return Boolean(product?.inventory && typeof product.inventory === 'object');
}

/**
 * Prefer catalog hydrate over line stubs; never drop thumb / inventory already present.
 *
 * @param {Object|null|undefined} base
 * @param {Object|null|undefined} overlay
 * @returns {Object|null}
 */
export function mergeFlyoutDisplayProduct(base, overlay) {
  if (!overlay) return base ?? null;
  if (!base) return overlay;

  const catalogFirst = !isFlyoutDisplayStub(base) ?
    base :
    (!isFlyoutDisplayStub(overlay) ? overlay : null);
  const primary = catalogFirst ?? overlay;
  const secondary = primary === overlay ? base : overlay;

  const imageGroups = catalogProductHasFlyoutThumb(primary) ?
    primary.imageGroups :
    (catalogProductHasFlyoutThumb(secondary) ?
      secondary.imageGroups :
      (primary.imageGroups ?? secondary.imageGroups));

  const inventory = catalogProductHasInventory(primary) ?
    primary.inventory :
    (catalogProductHasInventory(secondary) ?
      secondary.inventory :
      (primary.inventory ?? secondary.inventory));

  return {
    ...secondary,
    ...primary,
    name: primary.name || secondary.name || '',
    shortDescription: primary.shortDescription ?? secondary.shortDescription ?? null,
    imageGroups,
    inventory,
  };
}

/**
 * Line + master SKUs that still need a catalog GET.
 * A name-only stub is not enough — flyout needs thumb + inventory for OOS.
 * Masters are included when still stubs so GA4 `view_cart` / `remove_from_cart`
 * can reuse this hydrate instead of a second getProducts.
 *
 * @param {Object[]|null|undefined} lines
 * @param {Object<string, Object>|null|undefined} displayMap
 * @returns {string[]}
 */
export function collectMissingFlyoutProductIds(lines = [], displayMap = {}) {
  const missing = new Set();

  (lines ?? []).forEach((line) => {
    const productId = line?.productId;

    if (!productId) return;

    const cached = displayMap?.[productId];
    const hasThumb =
      basketLineHasFlyoutThumb(line) || catalogProductHasFlyoutThumb(cached);
    const hasInventory =
      (line?.inventory && typeof line.inventory === 'object') ||
      catalogProductHasInventory(cached);

    if (!(hasThumb && hasInventory)) {
      missing.add(productId);
    }

    const masterProductId = parseSelectedOptionsUIPayload(line?.c_selectedOptionsUI)?.masterProductId;

    if (masterProductId && isFlyoutDisplayStub(displayMap?.[masterProductId])) {
      missing.add(masterProductId);
    }
  });

  return [...missing];
}

/**
 * Keep a wider in-flight missing set — do not shrink to a subset (avoids a
 * second getProducts for ids already requested).
 *
 * @param {string} heldKey - comma-joined ids currently held
 * @param {string} nextKey - comma-joined ids from {@link collectMissingFlyoutProductIds}
 * @returns {string}
 */
export function stabilizeMissingFlyoutProductIdsKey(heldKey, nextKey) {
  if (!nextKey) {
    return '';
  }

  if (!heldKey) {
    return nextKey;
  }

  const prevIds = heldKey.split(',').filter(Boolean);
  const nextIds = nextKey.split(',').filter(Boolean);

  if (nextIds.every((id) => prevIds.includes(id))) {
    return heldKey;
  }

  return [...new Set([...prevIds, ...nextIds])].join(',');
}

/**
 * @param {Object<string, Object>|null|undefined} displayMap
 * @param {Object<string, Object>|null|undefined} fetchedMap
 * @returns {Object<string, Object>}
 */
export function mergeFlyoutProductMaps(displayMap = {}, fetchedMap = {}) {
  const ids = new Set([
    ...Object.keys(displayMap || {}),
    ...Object.keys(fetchedMap || {})
  ]);
  const merged = {};

  ids.forEach((id) => {
    merged[id] = mergeFlyoutDisplayProduct(displayMap?.[id], fetchedMap?.[id]);
  });

  return merged;
}

/**
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {Object<string, Object>|Object[]} productsOrMap
 */
export function seedCartFlyoutDisplay(queryClient, productsOrMap) {
  if (!queryClient) return;

  const incoming = Array.isArray(productsOrMap) ?
    productsOrMap.reduce((acc, product) => {
      if (product?.id) acc[product.id] = product;

      return acc;
    }, {}) :
    (productsOrMap || {});

  if (!Object.keys(incoming).length) return;

  queryClient.setQueryData(CART_FLYOUT_DISPLAY_QUERY_KEY, (current = {}) => mergeFlyoutProductMaps(current, incoming));
}

/**
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {Object[]|null|undefined} lines
 */
export function seedCartFlyoutDisplayFromLines(queryClient, lines) {
  seedCartFlyoutDisplay(queryClient, buildFlyoutDisplayMapFromLines(lines));
}
